import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import novasLogo from '../../assets/novas-logo.png';
import './Dictionary.css';
import '../DictionaryTest.css';
import FlashcardsGame from './FlashcardsGame';
import { getDictionary, addWord, deleteWord } from '../../utils/dictionaryService';
import { supabase } from '../../utils/supabaseClient';
import { fetchOpenAIText } from '../../utils/openaiClient';

export default function Dictionary({ studentData }) {
  // Get student ID (UUID) from studentData
  const getStudentId = () => {
    // Приоритет: studentData.id (UUID из Supabase)
    if (studentData?.id) {
      return studentData.id;
    }
    
    // Fallback: попробуем извлечь из URL или использовать slug
    const studentId = window.location.pathname.split('/').pop();
    if (studentId && studentId !== 'dictionary' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(studentId)) {
      return studentId;
    }
    
    return studentData?.slug || studentData?.originalSlug || null;
  };

  const [dictionaryEntries, setDictionaryEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dictionarySearchTerm, setDictionarySearchTerm] = useState('');
  const [isAddWordModalOpen, setIsAddWordModalOpen] = useState(false);
  const [newWord, setNewWord] = useState('');
  const [showFlashcardsGame, setShowFlashcardsGame] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [flashcardsStats, setFlashcardsStats] = useState({ correct: 0, incorrect: 0, gamesPlayed: 0 });
  const [wordVariants, setWordVariants] = useState([]);
  const [showVariantsModal, setShowVariantsModal] = useState(false);
  const [pendingWord, setPendingWord] = useState('');
  const [isLoadingVariants, setIsLoadingVariants] = useState(false);
  const [error, setError] = useState(null);
  const [selectedVariantIndices, setSelectedVariantIndices] = useState(new Set());

  // Функция нормализации ID - всегда возвращает UUID
  const normalizeStudentId = async (id) => {
    if (!id) return null;
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    if (isUUID) return id;
    
    try {
      const { data } = await supabase
        .from('students')
        .select('id')
        .eq('invite_slug', id)
        .maybeSingle();
      return data?.id || id;
    } catch (error) {
      console.error('Error normalizing student ID:', error);
      return id;
    }
  };

  // Load dictionary entries from Supabase
  useEffect(() => {
    const studentId = getStudentId();
    if (!studentId) {
      console.warn('Dictionary: No student ID available');
      setIsLoading(false);
      return;
    }
    
    let isMounted = true;
    
    const loadDictionary = async () => {
      console.log('Dictionary: Loading entries for student:', studentId);
      setIsLoading(true);
      
      try {
        const entries = await getDictionary(studentId);
        console.log('Dictionary: Loaded entries from Supabase:', entries);
        if (isMounted) {
          setDictionaryEntries(Array.isArray(entries) ? entries : []);
        }
      } catch (error) {
        console.error('Dictionary: Error loading entries:', error);
        if (isMounted) {
          setDictionaryEntries([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    loadDictionary();
    
    // Слушаем обновления словаря из других компонентов
    const handleDictionaryUpdate = async (e) => {
      const updatedStudentId = e.detail?.studentId;
      const currentStudentId = getStudentId();
      const normalizedUpdatedId = await normalizeStudentId(updatedStudentId);
      const normalizedCurrentId = await normalizeStudentId(currentStudentId);
      
      // Сравниваем нормализованные ID
      if (normalizedUpdatedId === normalizedCurrentId) {
        loadDictionary();
      }
    };
    
    window.addEventListener('dictionaryUpdated', handleDictionaryUpdate);
    
    return () => {
      isMounted = false;
      window.removeEventListener('dictionaryUpdated', handleDictionaryUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentData?.id]);

  const handleOpenAddWordModal = () => {
    setIsAddWordModalOpen(true);
    setNewWord('');
  };

  const handleCloseAddWordModal = () => {
    console.log('🔄 handleCloseAddWordModal called, isLoadingVariants:', isLoadingVariants);
    if (!isLoadingVariants) {
      console.log('✅ Closing modal (not loading)');
    setIsAddWordModalOpen(false);
      setError(null);
      setNewWord('');
    } else {
      console.log('⚠️ Modal close blocked (loading in progress)');
    }
  };

  const handleAddWord = async (e) => {
      e.preventDefault();
      e.stopPropagation();
    
    const trimmedWord = newWord.trim();
    if (!trimmedWord) {
      return;
    }
    
    // Защита от повторной отправки
    if (isLoadingVariants) {
      console.log('Already loading, ignoring duplicate submit');
      return;
    }
    
    const studentId = getStudentId();
    if (!studentId) {
      console.error('Cannot add word: student ID not available');
      setError('Student ID not available');
      return;
    }
    
    console.log('🔄 Modal should stay open, isLoadingVariants:', isLoadingVariants);
    setIsLoadingVariants(true);
    setError(null);
    console.log('🔄 After setIsLoadingVariants(true), isAddWordModalOpen should still be true');

    const word = trimmedWord;
    console.log('🧪 Starting word test:', word);
    
    // First, request all possible parts of speech
    try {
      const variantsPrompt = `List all possible parts of speech for the word or phrase "${word}". For each meaning, determine:
1. Part of speech (noun, verb, adjective, adverb, phrasal verb, idiom, etc.)
2. Type: if it's a phrasal verb or idiom, indicate this in the "type" field
3. CEFR level (A1, A2, B1, B2, C1, or C2)
4. Russian translation - IMPORTANT: include ALL meanings and translation variants with detailed context in parentheses, do NOT shorten translations
5. Example sentence in English (8-15 words)

Respond ONLY with valid JSON in format:
{
  "variants": [
    {
      "partOfSpeech": "noun",
      "type": "regular",
      "level": "A1",
      "translation": "work (process of labor, occupation, activity)",
      "example": "I have a lot of work to do."
    },
    {
      "partOfSpeech": "phrasal verb",
      "type": "phrasal verb",
      "level": "B1",
      "translation": "work on something, develop",
      "example": "We need to work on this project."
    },
    {
      "partOfSpeech": "idiom",
      "type": "idiom",
      "level": "B2",
      "translation": "work like clockwork (work perfectly, without failures)",
      "example": "The new system works like a charm."
    }
  ]
}`;

      console.log('🔄 Requesting all parts of speech variants...');
      
      const content = await fetchOpenAIText({
        prompt: variantsPrompt,
        temperature: 0.7,
        maxTokens: 800,
      });
      
      if (!content) {
        throw new Error('No response from OpenAI API');
      }

      let contentStr = content.trim();
      
      // Parse variants
      let jsonStr = contentStr;
      if (jsonStr.startsWith('```json')) {
        jsonStr = jsonStr.replace(/^```json\s*/gi, '').replace(/\s*```/g, '');
      } else if (jsonStr.startsWith('```')) {
        jsonStr = jsonStr.replace(/^```\s*/g, '').replace(/\s*```/g, '');
      }
      
      const match = jsonStr.match(/\{[\s\S]*\}/);
      if (match) {
        const parsed = JSON.parse(match[0]);
        
        if (parsed.variants && Array.isArray(parsed.variants) && parsed.variants.length > 0) {
          // If multiple variants - show modal for selection
          if (parsed.variants.length > 1) {
            setWordVariants(parsed.variants);
            setPendingWord(word);
            setIsLoadingVariants(false);
            setIsAddWordModalOpen(false); // Close add word modal
            setNewWord('');
            setShowVariantsModal(true); // Show variant selection modal
            return;
      } else {
            // If only one variant - add immediately
            const variant = parsed.variants[0];
            await addWordVariant(variant, word, studentId);
            setIsLoadingVariants(false);
            setIsAddWordModalOpen(false); // Close modal after successful addition
            setNewWord('');
            return;
          }
        }
      }
      
      // If failed to parse variants, use old method
      console.warn('⚠️ Failed to get variants, using old method');
    } catch (err) {
      console.error('❌ Error getting variants:', err);
      setError(err.message || 'Error getting word variants');
      setIsLoadingVariants(false);
      // Modal stays open so user can see the error
      return;
    }

    // Old method (if failed to get variants)
    try {
      await handleSingleWord(word, studentId);
    } catch (err) {
      console.error('❌ Error in handleSingleWord:', err);
      setError(err.message || 'Error adding word');
    } finally {
      setIsLoadingVariants(false);
    }
  };

  const handleSingleWord = async (word, studentId) => {
    // Old method - single request for one value
    try {
      const prompt = `For the English word or phrase "${word}":
1. Determine if it is a phrasal verb, idiom, or regular word/phrase
2. Determine its CEFR level (A1, A2, B1, B2, C1, or C2) based on word frequency dictionaries
3. Provide its Russian translation - IMPORTANT: include ALL meanings and translation variants with detailed context in parentheses, do NOT shorten translations
4. Create a natural English example sentence (8-15 words)

Respond ONLY with valid JSON in this format: {"type": "regular/phrasal verb/idiom", "level": "X", "translation": "Y", "example": "Z"}`;

      console.log('🔄 Making request via proxy...');
      
      const content = await fetchOpenAIText({
        prompt,
        temperature: 0.7,
        maxTokens: 500,
      });
      
      if (!content) {
        throw new Error('No response from OpenAI API');
      }

      let contentStr = content.trim();

      console.log('✅ Raw response received:', contentStr);
      
      // Parse JSON from response
      let level = 'B1';
      let translation = 'Перевод недоступен';
      let example = null;
      let type = 'regular';
      let partOfSpeech = null;
      
      try {
        let jsonStr = contentStr;
        if (jsonStr.startsWith('```json')) {
          jsonStr = jsonStr.replace(/^```json\s*/gi, '').replace(/\s*```/g, '');
        } else if (jsonStr.startsWith('```')) {
          jsonStr = jsonStr.replace(/^```\s*/g, '').replace(/\s*```/g, '');
        }
        
        const match = jsonStr.match(/\{[\s\S]*\}/);
        if (match) {
          const parsed = JSON.parse(match[0]);
          
          if (parsed.type && ['phrasal verb', 'idiom', 'regular'].includes(parsed.type.toLowerCase())) {
            type = parsed.type.toLowerCase();
          }
          
          if (parsed.level && /^[ABC]\d$/.test(parsed.level)) {
            level = parsed.level;
          }
          
          if (parsed.translation) {
            let translationText = parsed.translation.trim();
            if (Array.isArray(parsed.translation)) {
              translationText = parsed.translation
                .map(t => typeof t === 'string' ? t.trim() : String(t).trim())
                .filter(t => t.length > 0)
                .join(', ');
            } else if (typeof translationText === 'string') {
              translationText = translationText
                .replace(/\s+/g, ' ')
                .replace(/,\s*,/g, ',')
                  .trim();
              }
            if (translationText && translationText.length > 0) {
              translation = translationText;
            }
          }
          
          if (parsed.example && parsed.example.trim()) {
            example = parsed.example.trim();
          }

          if (parsed.partOfSpeech && parsed.partOfSpeech.trim()) {
            partOfSpeech = parsed.partOfSpeech.trim();
          }
            } else {
          translation = content;
        }
      } catch (parseErr) {
        console.error('❌ Error parsing JSON:', parseErr);
        translation = content;
      }
      
      await addWordVariant({ level, translation, example, type, partOfSpeech }, word, studentId);
      setIsLoadingVariants(false);
      setIsAddWordModalOpen(false); // Close modal after successful addition
      setNewWord('');
      setError(null);
    } catch (err) {
      const errorMsg = err.message || 'Error getting data';
      setError(errorMsg);
      setIsLoadingVariants(false);
      console.error('❌ Error testing word:', err);
      // Modal stays open so user can see the error
    }
  };

  const addWordVariant = async (variant, word, studentId) => {
    try {
      console.log('🔄 Adding word variant:', { word, variant, studentId });
      const newEntry = await addWord(studentId, {
        word: word,
        translation: variant.translation || 'Translation not available',
        level: variant.level || 'B1',
        example: variant.example || null,
        type: variant.type || 'regular',
        partOfSpeech: variant.partOfSpeech || null
      });
      
      console.log('✅ Word added successfully:', newEntry);
      
      // Перезагружаем словарь из Supabase
      console.log('🔄 Reloading dictionary...');
      const entries = await getDictionary(studentId);
      console.log('✅ Dictionary reloaded, entries count:', entries.length);
      setDictionaryEntries(Array.isArray(entries) ? entries : []);
      
      // Событие уже отправлено в dictionaryService.js с нормализованным ID
    } catch (error) {
      console.error('❌ Error adding word variant:', error);
      const errorMessage = error.message || 'Failed to add word to dictionary';
      setError(errorMessage);
      alert(`Failed to save word to Supabase: ${errorMessage}. Please check your connection and try again.`);
      throw error;
    }
  };

  const closeVariantsModal = () => {
    setShowVariantsModal(false);
    setWordVariants([]);
    setPendingWord('');
    setSelectedVariantIndices(new Set());
  };

  const toggleVariantIndex = (idx) => {
    setSelectedVariantIndices(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const handleSelectSelected = async () => {
    const studentId = getStudentId();
    if (!studentId) return;
    const toAdd = wordVariants.filter((_, i) => selectedVariantIndices.has(i));
    for (const variant of toAdd) {
      await addWordVariant(variant, pendingWord, studentId);
    }
    closeVariantsModal();
  };

  const handleSelectAllVariants = async () => {
    const studentId = getStudentId();
    if (!studentId) return;
    for (const variant of wordVariants) {
      await addWordVariant(variant, pendingWord, studentId);
    }
    closeVariantsModal();
  };

  const normalizePos = (pos) => {
    if (!pos) return null;
    const p = pos.toLowerCase().trim();
    if (p.includes('phrasal')) return 'phrasal verb';
    if (p.includes('idiom')) return 'idiom';
    if (p.includes('noun') || p.includes('substantive')) return 'noun';
    if (p.includes('verb')) return 'verb';
    if (p.includes('adjective') || p === 'adj') return 'adjective';
    if (p.includes('adverb') || p === 'adv') return 'adverb';
    if (p.includes('pronoun')) return 'pronoun';
    if (p.includes('preposition') || p === 'prep') return 'preposition';
    if (p.includes('conjunction') || p === 'conj') return 'conjunction';
    if (p.includes('interjection') || p.includes('exclamation')) return 'interjection';
    return p;
  };

  const translationWords = (text) => {
    if (!text) return new Set();
    return new Set(
      text.toLowerCase()
        .replace(/[()[\],.;:!?–—-]/g, ' ')
        .split(/\s+/)
        .filter(w => w.length > 2)
    );
  };

  const translationSimilarity = (a, b) => {
    const wa = translationWords(a);
    const wb = translationWords(b);
    if (!wa.size && !wb.size) return 1;
    if (!wa.size || !wb.size) return 0;
    const intersection = [...wa].filter(w => wb.has(w)).length;
    const union = new Set([...wa, ...wb]).size;
    return intersection / union;
  };

  const isVariantAlreadyAdded = (variant) => {
    const word = pendingWord.toLowerCase();
    const variantPos = normalizePos(variant.partOfSpeech);
    const variantType = variant.type || 'regular';
    return dictionaryEntries.some(entry => {
      if (entry.word?.toLowerCase() !== word) return false;
      // Primary: normalized part of speech match
      const entryPos = normalizePos(entry.partOfSpeech);
      if (entryPos && variantPos && entryPos === variantPos) return true;
      // Secondary: type match (phrasal verb / idiom / regular)
      if ((entry.type || 'regular') === variantType && variantType !== 'regular') return true;
      // Tertiary: translation similarity — catches AI inconsistency in labels
      if (translationSimilarity(entry.translation, variant.translation) >= 0.3) return true;
      return false;
    });
  };

  const handleDeleteWord = async (id) => {
    try {
      await deleteWord(id);
      
      // Reload dictionary from Supabase
      const studentId = getStudentId();
      if (studentId) {
        const entries = await getDictionary(studentId);
        setDictionaryEntries(Array.isArray(entries) ? entries : []);
      } else {
        // Fallback: remove from local state
        setDictionaryEntries(entries => entries.filter(entry => entry.id !== id));
      }
    } catch (error) {
      console.error('Error deleting word:', error);
      // In case of error, still remove from local state
    setDictionaryEntries(entries => entries.filter(entry => entry.id !== id));
    }
  };

  // Handle pronunciation
  const handlePronunciation = (word) => {
    const utterance = new SpeechSynthesisUtterance(word);
    utterance.lang = 'en-US';
    speechSynthesis.speak(utterance);
  };

  // getWordDetails теперь импортируется из utils/getWordDetails.js

  // Handle word input change
  const handleWordInputChange = (e) => {
    setNewWord(e.target.value);
  };

  // Filtered entries
  const filteredEntries = dictionaryEntries.filter(entry => {
    const q = dictionarySearchTerm.trim().toLowerCase();
    if (!q) return true;
    const haystack = [entry.word, entry.translation, entry.example, entry.lesson?.title]
      .map(v => (v || '').toLowerCase());
    return haystack.some(s => s.includes(q));
  });

  if (isLoading) {
    return (
      <div className="dictionary-page">
        <div className="dictionary-banner">
          <div className="dictionary-banner-content">
            <h2 className="dictionary-title-banner">Dictionary</h2>
            <p className="dictionary-subtitle">Your personal vocabulary collection</p>
          </div>
          <div className="dictionary-novas-banner-logo">
            <img src={novasLogo} alt="Novas Logo" />
          </div>
        </div>
        <div className="dictionary-content" style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading dictionary...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dictionary-page">
      <div className="dictionary-banner">
        <div className="dictionary-banner-content">
          <h2 className="dictionary-title-banner">Dictionary</h2>
          <p className="dictionary-subtitle">Your personal vocabulary collection</p>
        </div>
        
        <div className="dictionary-novas-banner-logo">
          <img src={novasLogo} alt="Novas Logo" />
        </div>
      </div>

              <div className="dictionary-content">
          <div className="dictionary-stats-section">
            <div className="dictionary-stat-card">
              <div className="stat-content">
                <p className="stat-value">{dictionaryEntries.length}</p>
                <div className="stat-label">
                  <div>Total</div>
                  <div>Words</div>
                </div>
              </div>
            </div>
            <div className="dictionary-stat-card">
              <div className="stat-content">
                <p className="stat-value">
                  {(() => {
                    const currentStudentId = getStudentId();
                    const savedLearnedWords = currentStudentId && currentStudentId !== 'new'
                      ? localStorage.getItem(`student-learned-words_${currentStudentId}`)
                      : null;
                    if (savedLearnedWords) {
                      try {
                        const learnedWords = JSON.parse(savedLearnedWords);
                        return learnedWords.length || 0;
                      } catch (e) {
                        return 0;
                      }
                    }
                    return 0;
                  })()}
                </p>
                <div className="stat-label">
                  <div>Words</div>
                  <div>Learned</div>
                </div>
              </div>
            </div>
            <div className="dictionary-stat-card">
              <div className="stat-content">
                <p className="stat-value">
                  {dictionaryEntries.length > 0 ? 
                    Math.round((dictionaryEntries.filter(entry => entry.level === 'A1' || entry.level === 'A2' || entry.level === 'Beginner').length / dictionaryEntries.length) * 100) : 0
                  }%
                </p>
                <div className="stat-label">
                  <div>Beginner</div>
                  <div>Level</div>
                </div>
              </div>
            </div>
            <div className="dictionary-stat-card">
              <div className="stat-content">
                <p className="stat-value">
                  {dictionaryEntries.length > 0 ? 
                    Math.round((dictionaryEntries.filter(entry => entry.level === 'B1' || entry.level === 'B2' || entry.level === 'Intermediate').length / dictionaryEntries.length) * 100) : 0
                  }%
                </p>
                <div className="stat-label">
                  <div>Intermediate</div>
                  <div>Level</div>
                </div>
              </div>
            </div>
            <div className="dictionary-stat-card">
              <div className="stat-content">
                <p className="stat-value">
                  {dictionaryEntries.length > 0 ? 
                    Math.round((dictionaryEntries.filter(entry => entry.level === 'C1' || entry.level === 'C2' || entry.level === 'Advanced').length / dictionaryEntries.length) * 100) : 0
                  }%
                </p>
                <div className="stat-label">
                  <div>Advanced</div>
                  <div>Level</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="dictionary-header">
            <div className="search-section">
              <input
                type="text"
                placeholder="Search words..."
                className="dictionary-search-input"
                value={dictionarySearchTerm}
                onChange={(e) => setDictionarySearchTerm(e.target.value)}
              />
            </div>
            <div className="dictionary-actions-header">
              <button
                className="student-flashcards-btn"
                onClick={() => setShowFlashcardsGame(true)}
                disabled={dictionaryEntries.length === 0}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14" height="14" width="14">
                  <desc>
                    Cards Streamline Icon: https://streamlinehq.com
                  </desc>
                  <g id="cards">
                    <path id="Union" fill="currentColor" fillRule="evenodd" d="M7.56075 0.329923c-0.25584 -0.068516 -0.51879 0.083297 -0.58737 0.339116L4.63904 9.37652c-0.06861 0.25591 0.08327 0.51897 0.3392 0.58751l6.08346 1.62927c0.2559 0.0685 0.5188 -0.0833 0.5874 -0.3391l2.3344 -8.70752c0.0686 -0.25591 -0.0833 -0.51897 -0.3392 -0.58751L7.56075 0.329923ZM3.43167 9.05281l1.59639 -5.95479L0.355742 4.34933c-0.2559281 0.06854 -0.4078085 0.33161 -0.3392026 0.58752L2.35089 13.6443c0.06858 0.2558 0.33153 0.4077 0.58736 0.3391l6.0835 -1.6292c0.00794 -0.0021 0.01579 -0.0044 0.02352 -0.0069l-4.3904 -1.1758c-0.9229 -0.2472 -1.4706 -1.19585 -1.2232 -2.11869Z" clipRule="evenodd" strokeWidth="1"></path>
                  </g>
                </svg>
                Flashcards Game
              </button>
              <button className="student-add-new-words-btn" onClick={handleOpenAddWordModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4V20M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Add New Word
              </button>
            </div>
          </div>

        <div className="student-dictionary-table-container">
          {filteredEntries.length > 0 ? (
            <>
              <div className="student-dictionary-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Word</th>
                      <th>Part of Speech</th>
                      <th>Level</th>
                      <th>Translation</th>
                      <th>Example</th>
                      <th>Lesson</th>
                      <th style={{ textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEntries.map(entry => (
                      <tr key={entry.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span>{entry.word}</span>
                            {entry.type && entry.type !== 'regular' && (
                              <span className={`dictionary-test-type-badge dictionary-test-type-${entry.type.replace(/\s+/g, '-').toLowerCase()}`} style={{ marginLeft: '8px' }}>
                                {entry.type === 'phrasal verb' ? 'Phrasal verb' : entry.type === 'idiom' ? 'Idiom' : entry.type}
                              </span>
                            )}
                                <button
                                  className="student-audio-play-btn"
                                  title="Play pronunciation"
                                  onClick={() => handlePronunciation(entry.word)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#6c757d',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.color = '#ee5a24';
                                    e.target.style.backgroundColor = 'rgba(238, 90, 36, 0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.color = '#6c757d';
                                    e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                  </svg>
                                </button>
                              </div>
                            </td>
                            <td>
                          {entry.partOfSpeech ? (
                            <span className="dictionary-test-pos-badge">
                              {entry.partOfSpeech}
                            </span>
                          ) : (
                            <span style={{ color: '#999' }}>—</span>
                          )}
                        </td>
                        <td>
                          <span className={`student-level-badge student-level-${(entry.level || 'na').toLowerCase()}`}>
                            {entry.level || 'N/A'}
                              </span>
                            </td>
                                                         <td>{entry.translation}</td>
                             <td>{entry.example}</td>
                             <td>{entry.lesson?.title || ''}</td>
                             <td className="student-dictionary-actions">
                              <button
                                className="student-action-btn student-delete-btn"
                                title="Delete"
                                onClick={() => handleDeleteWord(entry.id)}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM8 9H16V19H8V9ZM15.5 4L14.5 3H9.5L8.5 4H5V6H19V4H15.5Z" fill="currentColor"/>
                                </svg>
                              </button>
                            </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="student-no-lessons-message">No dictionary entries found.</p>
          )}
        </div>
      </div>

      {isAddWordModalOpen && ReactDOM.createPortal(
        <div
          className="student-modal-overlay"
          onClick={(e) => {
            if (!isLoadingVariants && e.target === e.currentTarget) {
              handleCloseAddWordModal();
            }
          }}
        >
          <div className="student-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="student-modal-header">
              <h2>Add New Word</h2>
              <button 
                className="student-close-modal-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isLoadingVariants) {
                    handleCloseAddWordModal();
                  }
                }}
                disabled={isLoadingVariants}
                style={{ opacity: isLoadingVariants ? 0.5 : 1, cursor: isLoadingVariants ? 'not-allowed' : 'pointer' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <form 
              onSubmit={handleAddWord} 
              className="student-add-word-form"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="student-form-group">
                <input
                  type="text"
                  value={newWord}
                  onChange={handleWordInputChange}
                  placeholder="Enter word or phrase"
                  required
                  autoFocus
                  disabled={isLoadingVariants}
                  className="student-word-input"
                />
              </div>
              {isLoadingVariants && (
                <div style={{ textAlign: 'center', padding: '10px', color: '#666' }}>
                  <div style={{ display: 'inline-block', marginRight: '10px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="31.416" strokeDashoffset="31.416" opacity="0.3"/>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="31.416" strokeDashoffset="23.562" strokeLinecap="round"/>
                    </svg>
                  </div>
                  Loading word details...
                </div>
              )}
              {error && !isLoadingVariants && (
                <div style={{ 
                  padding: '10px', 
                  margin: '10px 0', 
                  backgroundColor: '#fee', 
                  border: '1px solid #fcc', 
                  borderRadius: '4px', 
                  color: '#c33' 
                }}>
                  <strong>Error:</strong> {error}
                </div>
              )}
              <div className="student-form-actions">
                <button 
                  type="button" 
                  onClick={handleCloseAddWordModal} 
                  className="student-cancel-btn"
                  disabled={isLoadingVariants}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="student-submit-btn"
                  disabled={isLoadingVariants}
                >
                  {isLoadingVariants ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ display: 'inline-block', marginRight: '8px', animation: 'spin 1s linear infinite', verticalAlign: 'middle' }}>
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="31.416" strokeDashoffset="31.416" opacity="0.3"/>
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="31.416" strokeDashoffset="23.562" strokeLinecap="round"/>
                      </svg>
                      Loading...
                    </>
                  ) : (
                    'Add Word'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {showFlashcardsGame && (
        <FlashcardsGame
          dictionaryEntries={filteredEntries}
          onClose={() => setShowFlashcardsGame(false)}
          studentId={getStudentId()}
        />
      )}

      {/* Modal for selecting part of speech variant (like in DictionaryTest) */}
      {showVariantsModal && wordVariants && wordVariants.length > 0 && ReactDOM.createPortal(
        <div className="dictionary-test-modal-overlay" onClick={() => {
          console.log('Closing modal');
          closeVariantsModal();
        }}>
          <div className="dictionary-test-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dictionary-test-modal-header">
              <h2>Select variants for "{pendingWord}"</h2>
              <button className="dictionary-test-modal-close" onClick={closeVariantsModal}>×</button>
            </div>
            <div className="dictionary-test-modal-content">
              <div className="dictionary-test-variants-list">
                {wordVariants.map((variant, index) => {
                  const alreadyAdded = isVariantAlreadyAdded(variant);
                  const checked = selectedVariantIndices.has(index);
                  return (
                    <div
                      key={index}
                      className="dictionary-test-variant-item"
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        cursor: alreadyAdded ? 'default' : 'pointer',
                        background: alreadyAdded ? '#f8fafc' : checked ? '#f0fdf4' : undefined,
                        borderColor: alreadyAdded ? '#e2e8f0' : checked ? '#22c55e' : undefined,
                        opacity: alreadyAdded ? 0.65 : 1,
                      }}
                      onClick={() => !alreadyAdded && toggleVariantIndex(index)}
                    >
                      <div style={{ marginTop: 2, flexShrink: 0 }}>
                        {alreadyAdded ? (
                          <span style={{ fontSize: 16, color: '#22c55e' }}>✓</span>
                        ) : (
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleVariantIndex(index)}
                            onClick={e => e.stopPropagation()}
                            style={{ width: 16, height: 16, accentColor: '#22c55e', cursor: 'pointer' }}
                          />
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="dictionary-test-variant-header">
                          {variant.type && variant.type !== 'regular' && (
                            <span className={`dictionary-test-type-badge dictionary-test-type-${variant.type.replace(/\s+/g, '-').toLowerCase()}`}>
                              {variant.type === 'phrasal verb' ? 'Phrasal verb' : variant.type === 'idiom' ? 'Idiom' : variant.type}
                            </span>
                          )}
                          <span className="dictionary-test-pos-badge">{variant.partOfSpeech || 'N/A'}</span>
                          <span className={`student-level-badge student-level-${(variant.level || 'na').toLowerCase()}`}>
                            {variant.level || 'N/A'}
                          </span>
                          {alreadyAdded && (
                            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#22c55e', fontWeight: 600, background: '#f0fdf4', borderRadius: 4, padding: '1px 6px', border: '1px solid #bbf7d0' }}>
                              Already added
                            </span>
                          )}
                        </div>
                        <div className="dictionary-test-variant-translation">
                          <strong>Translation:</strong> {variant.translation || 'N/A'}
                        </div>
                        <div className="dictionary-test-variant-example">
                          <strong>Example:</strong> {variant.example || 'N/A'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="dictionary-test-modal-actions" style={{ gap: 8 }}>
                <button
                  className="dictionary-test-button"
                  disabled={selectedVariantIndices.size === 0}
                  onClick={handleSelectSelected}
                  style={{ background: selectedVariantIndices.size > 0 ? '#22c55e' : '#d1d5db', color: '#fff', opacity: selectedVariantIndices.size > 0 ? 1 : 0.6 }}
                >
                  Add selected{selectedVariantIndices.size > 0 ? ` (${selectedVariantIndices.size})` : ''}
                </button>
                <button
                  className="dictionary-test-button dictionary-test-select-all-button"
                  onClick={handleSelectAllVariants}
                >
                  Add all
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
